// ============================================================================
// Daily-photo URL consumer registry
// ============================================================================
//
// Daily-report photo thumbnails are a shared, component-scoped sidecar. They
// are not route resources: a page that still has legacy data reads must not
// gain a permanent-ownership claim merely because its visible daily-photo
// thumbnails have a scoped URL owner. The finite registry below is the receipt
// source for every real DailyPhotoThumbnails JSX mount.
// ============================================================================

/** The one private URL resource every automatic daily-photo thumbnail uses. */
export const DAILY_PHOTO_URL_RESOURCE = Object.freeze({
  SIGNED_URL: 'shared.daily-photo.signed-url',
});

export const DAILY_PHOTO_THUMBNAIL_MOUNT = Object.freeze({
  DETAIL: 'detail',
  EDIT_MODAL: 'edit-modal',
});

export type DailyPhotoThumbnailMount = (typeof DAILY_PHOTO_THUMBNAIL_MOUNT)[keyof typeof DAILY_PHOTO_THUMBNAIL_MOUNT];

export type DailyPhotoThumbnailConsumer = {
  readonly source: string;
  readonly surface: string;
  readonly mount: DailyPhotoThumbnailMount;
};

// One entry per daily-photo thumbnail JSX mount, including legacy surfaces.
// (E12a deleted the broiler, layer, pig and sheep hubs' unreachable edit
// modals and with them their edit-modal mounts; cattle's edit modal is live.)
// `surface` tells the receipt where the shared component is reachable; it does
// not say that the component owns that surface's route data or boot release.
export const DAILY_PHOTO_THUMBNAIL_CONSUMERS: readonly DailyPhotoThumbnailConsumer[] = Object.freeze([
  Object.freeze({
    source: 'src/broiler/PoultryDailyPage.tsx',
    surface: 'broiler.dailys.detail',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.DETAIL,
  }),
  Object.freeze({
    source: 'src/cattle/CattleDailysView.tsx',
    surface: 'cattle.dailys.index',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.EDIT_MODAL,
  }),
  Object.freeze({
    source: 'src/cattle/CattleDailyPage.tsx',
    surface: 'cattle.dailys.detail',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.DETAIL,
  }),
  Object.freeze({
    source: 'src/layer/LayerDailyPage.tsx',
    surface: 'layer.dailys.detail',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.DETAIL,
  }),
  Object.freeze({
    source: 'src/pig/PigDailyPage.tsx',
    surface: 'pig.dailys.detail',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.DETAIL,
  }),
  Object.freeze({
    source: 'src/sheep/SheepDailyPage.tsx',
    surface: 'sheep.dailys.detail',
    mount: DAILY_PHOTO_THUMBNAIL_MOUNT.DETAIL,
  }),
]);
